import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { HeroFull } from "@/components/HeroFull";
import { Mosaic } from "@/components/Mosaic";
import { Testimonials } from "@/components/Testimonials";
import { useContent } from "@/context/ContentContext";
import { assetUrl } from "@/lib/api";

export default function Home() {
  const { content: c } = useContent();

  return (
    <div data-testid="home-page">
      {/* SECTION 2 — HERO */}
      <HeroFull media={c.media_hero_full} />

      {/* SECTION 3 — WELCOME / INTRODUCTION */}
      <section className="pt-16 md:pt-24 pb-10 md:pb-16 container-lux text-center" data-testid="welcome-section">
        <Reveal>
          <h2 className="font-serif-lux text-4xl md:text-6xl font-normal uppercase leading-[1.08] text-[#47562F] max-w-3xl mx-auto mb-10 md:mb-14">
            {c.home_welcome_heading}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#3A3A34] text-lg md:text-xl leading-loose max-w-2xl mx-auto mb-8">
            {c.home_intro_p1}
          </p>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="text-[#3A3A34] text-lg md:text-xl leading-loose max-w-2xl mx-auto mb-12 md:mb-16">
            {c.home_intro_p2}
          </p>
        </Reveal>

        {/* SECTION 4 — EXPLORE BUTTON + 2×3 MOSAIC */}
        <Reveal delay={0.22}>
          <div className="flex justify-center mb-14 md:mb-20">
            <Link to="/services" data-testid="welcome-explore-link" className="btn-gold w-full max-w-lg !py-5 !text-sm md:!text-base gap-3 group">
              {c.home_explore_label || "Explore"} <ArrowRight size={18} className="group-hover:translate-x-1.5 transition-transform" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Mosaic items={c.media_mosaic} />
        </Reveal>
      </section>

      {/* SECTION 5 — SPECIAL MOMENTS INTRO */}
      <section className="pt-10 md:pt-16 pb-12 md:pb-16 container-lux text-center" data-testid="moments-intro">
        <Reveal>
          <p className="overline mb-6">{c.home_moments_overline}</p>
          <h2 className="font-serif-lux text-3xl md:text-5xl font-light text-[#47562F] max-w-3xl mx-auto leading-tight">
            {c.home_moments_heading}
          </h2>
        </Reveal>
      </section>

      {/* SECTION 6 — WEDDINGS & EVENTS CATEGORIES */}
      <section className="pb-24 md:pb-36 container-lux" data-testid="moments-categories">
        <div className="space-y-16 md:space-y-24">
          {[
            { to: "/weddings", label: c.home_cat_weddings_label || "Weddings", img: c.moments_weddings_image, testid: "moment-weddings" },
            { to: "/events", label: c.home_cat_events_label || "Events", img: c.moments_events_image, testid: "moment-events" },
          ].map((cat, i) => (
            <Reveal key={cat.to} delay={0.05}>
              <div data-testid={cat.testid}>
                <Link to={cat.to} className="group block">
                  <figure className="editorial-photo w-full aspect-[16/10] md:aspect-[16/9] media-zoom">
                    {cat.img ? (
                      <img src={assetUrl(cat.img)} alt={cat.label} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full hero-full-placeholder" />
                    )}
                    <span className="editorial-grade" />
                  </figure>
                </Link>
                <Link
                  to={cat.to}
                  data-testid={`${cat.testid}-link`}
                  className="group inline-flex items-center gap-3 mt-6 font-serif-lux text-3xl md:text-4xl font-light text-[#1A1A1A] hover:text-[#8A9A6B] transition-colors uppercase tracking-[0.04em]"
                >
                  {cat.label} <ArrowRight size={26} className="text-[#8A9A6B] group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECTION 8 — TESTIMONIALS (unchanged) */}
      <Testimonials />

      {/* SECTION 9 — FINAL CTA */}
      <section className="relative py-28 md:py-44 overflow-hidden" data-testid="cta-section">
        {c.home_cta_image ? (
          <img src={assetUrl(c.home_cta_image)} alt="Celebration" className="img-graded absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 hero-full-placeholder" />
        )}
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative container-lux text-center">
          <Reveal>
            <h2 className="font-serif-lux text-white text-4xl md:text-6xl font-light mb-10 leading-tight">
              {c.home_cta_heading}<br /><span className="text-[#D4AF37]">{c.home_cta_highlight}</span>
            </h2>
            <Link to="/quote" data-testid="cta-quote-btn" className="btn-gold !px-10 !py-4">{c.home_cta_button || "Begin Your Journey"}</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
